/**
 * BIRHAUS Stub Components
 * 
 * Lightweight stub implementations for testing and prototyping.
 * These provide the same API as full components but with minimal functionality.
 * 
 * Usage:
 * ```ts
 * import { BirhausToastProviderStub, useBirhausToastStub } from '@birhaus/primitives/stubs'
 * 
 * // In tests or during development
 * <BirhausToastProviderStub>
 *   <App />
 * </BirhausToastProviderStub>
 * ```
 */

import React, { ReactNode, createContext, useContext } from 'react'
import type { BirhausToastData, BirhausToastProviderProps } from '../components/BirhausToast'

// Toast Provider Stub
export const BirhausToastProviderStub = ({ children }: { children: ReactNode }) => {
  console.warn('🔧 BIRHAUS: Using BirhausToastProviderStub - replace with full provider in production')
  return React.createElement(React.Fragment, null, children)
}

// Toast Context Stub
const ToastContextStub = createContext({
  toasts: [],
  addToast: () => 'stub-toast-id',
  dismissToast: () => {},
  clearAllToasts: () => {},
  locale: 'es' as const,
  setLocale: () => {}
})

// Toast Hook Stub
export const useBirhausToastStub = () => {
  console.warn('🔧 BIRHAUS: Using useBirhausToastStub - replace with full hook in production')
  
  return {
    // Core methods
    showToast: (toast: Omit<BirhausToastData, 'id'>) => {
      console.log('📢 Toast (stub):', toast.messageEs || toast.message)
      return 'stub-toast-id'
    },
    
    showSuccess: (message: string, options?: any) => {
      console.log('✅ Success (stub):', message)
      return 'stub-toast-id'
    },
    
    showError: (message: string, options?: any) => {
      console.error('❌ Error (stub):', message)
      return 'stub-toast-id'
    },
    
    showWarning: (message: string, options?: any) => {
      console.warn('⚠️ Warning (stub):', message)
      return 'stub-toast-id'
    },
    
    showInfo: (message: string, options?: any) => {
      console.info('ℹ️ Info (stub):', message)
      return 'stub-toast-id'
    },
    
    // Convenience methods
    showSaved: (entity?: string) => {
      const message = entity ? `${entity} guardado exitosamente` : 'Guardado exitosamente'
      console.log('💾 Saved (stub):', message)
      return 'stub-toast-id'
    },
    
    showDeleted: (entity?: string) => {
      const message = entity ? `${entity} eliminado exitosamente` : 'Eliminado exitosamente'
      console.log('🗑️ Deleted (stub):', message)
      return 'stub-toast-id'
    },
    
    showPaymentSuccess: (amount?: string) => {
      const message = amount ? `Pago de ${amount} procesado exitosamente` : 'Pago procesado exitosamente'
      console.log('💳 Payment (stub):', message)
      return 'stub-toast-id'
    },
    
    // Control methods
    dismissToast: (id: string) => {
      console.log('👋 Dismiss toast (stub):', id)
    },
    
    clearAllToasts: () => {
      console.log('🧹 Clear all toasts (stub)')
    },
    
    // Context
    locale: 'es' as const,
    toasts: [] as BirhausToastData[]
  }
}

// Button Hook Stub
export const useBirhausButtonStub = () => {
  console.warn('🔧 BIRHAUS: Using useBirhausButtonStub - replace with full hook in production')
  
  return {
    loading: false,
    success: false,
    executeAction: async (action: () => Promise<void>) => {
      console.log('🔄 Button action (stub): executing...')
      try {
        await action()
        console.log('✅ Button action (stub): completed')
      } catch (error) {
        console.error('❌ Button action (stub): failed', error)
      }
    },
    setLoading: () => {},
    setSuccess: () => {}
  }
}

// Data Table Hook Stub
export const useBirhausDataTableStub = <T = any>() => {
  console.warn('🔧 BIRHAUS: Using useBirhausDataTableStub - replace with full hook in production')
  
  return {
    data: [] as T[],
    filteredData: [] as T[],
    selectedItems: [] as T[],
    searchQuery: '',
    sortConfig: null,
    bulkActions: [],
    pagination: {
      page: 1,
      pageSize: 10,
      total: 0,
      totalPages: 0
    },
    
    // Methods
    setSearchQuery: (query: string) => console.log('🔍 Search (stub):', query),
    setSortConfig: (config: any) => console.log('📊 Sort (stub):', config),
    toggleSelection: (item: T) => console.log('☑️ Toggle selection (stub):', item),
    toggleAll: () => console.log('☑️ Toggle all (stub)'),
    executeBulkAction: (action: any) => console.log('📦 Bulk action (stub):', action),
    clearSelection: () => console.log('🧹 Clear selection (stub)'),
    goToPage: (page: number) => console.log('📄 Go to page (stub):', page),
    changePageSize: (size: number) => console.log('📏 Change page size (stub):', size)
  }
}

// Search Hook Stub
export const useBirhausSearchStub = () => {
  console.warn('🔧 BIRHAUS: Using useBirhausSearchStub - replace with full hook in production')
  
  return {
    query: '',
    results: [],
    suggestions: [],
    loading: false,
    setQuery: (query: string) => console.log('🔍 Search query (stub):', query),
    search: (query: string) => console.log('🔍 Executing search (stub):', query),
    clearSearch: () => console.log('🧹 Clear search (stub)'),
    selectSuggestion: (suggestion: any) => console.log('💡 Select suggestion (stub):', suggestion)
  }
}

// Form Hook Stub
export const useBirhausFormStub = () => {
  console.warn('🔧 BIRHAUS: Using useBirhausFormStub - replace with full hook in production')
  
  return {
    values: {},
    errors: {},
    touched: {},
    isValid: true,
    isSubmitting: false,
    setValue: (field: string, value: any) => console.log('📝 Set value (stub):', field, value),
    setError: (field: string, error: string) => console.log('❌ Set error (stub):', field, error),
    clearErrors: () => console.log('🧹 Clear errors (stub)'),
    validateField: (field: string) => console.log('✅ Validate field (stub):', field),
    validateForm: () => console.log('✅ Validate form (stub)'),
    resetForm: () => console.log('🔄 Reset form (stub)'),
    submitForm: () => console.log('📤 Submit form (stub)')
  }
}

// Alert Hook Stub
export const useBirhausAlertStub = () => {
  console.warn('🔧 BIRHAUS: Using useBirhausAlertStub - replace with full hook in production')
  
  return {
    visible: false,
    variant: 'info' as const,
    message: '',
    showAlert: (message: string, variant = 'info') => {
      console.log(`🚨 Alert (stub) [${variant}]:`, message)
    },
    hideAlert: () => console.log('👋 Hide alert (stub)'),
    clearAlert: () => console.log('🧹 Clear alert (stub)')
  }
}

// Global toast stub (similar to the imperative API)
export const toastStub = {
  success: (message: string, options?: any) => {
    console.log('✅ Toast Success (stub):', message)
    return 'stub-toast-id'
  },
  error: (message: string, options?: any) => {
    console.error('❌ Toast Error (stub):', message)
    return 'stub-toast-id'
  },
  warning: (message: string, options?: any) => {
    console.warn('⚠️ Toast Warning (stub):', message)
    return 'stub-toast-id'
  },
  info: (message: string, options?: any) => {
    console.info('ℹ️ Toast Info (stub):', message)
    return 'stub-toast-id'
  },
  dismiss: (id?: string) => {
    console.log('👋 Toast Dismiss (stub):', id)
  },
  dismissAll: () => {
    console.log('🧹 Toast Dismiss All (stub)')
  },
  saved: (entity?: string) => {
    const message = entity ? `${entity} guardado exitosamente` : 'Guardado exitosamente'
    console.log('💾 Toast Saved (stub):', message)
    return 'stub-toast-id'
  },
  deleted: (entity?: string) => {
    const message = entity ? `${entity} eliminado exitosamente` : 'Eliminado exitosamente'
    console.log('🗑️ Toast Deleted (stub):', message)
    return 'stub-toast-id'
  }
}

// Component stubs for quick replacement
export const BirhausButtonStub = ({ children, onClick, ...props }: any) => {
  console.warn('🔧 BIRHAUS: Using BirhausButtonStub - replace with full component in production')
  return React.createElement('button', {
    onClick: (e: any) => {
      console.log('🔘 Button click (stub)')
      onClick?.(e)
    },
    className: "px-4 py-2 bg-gray-200 rounded border",
    ...props
  }, children)
}

export const BirhausInputStub = ({ onChange, ...props }: any) => {
  console.warn('🔧 BIRHAUS: Using BirhausInputStub - replace with full component in production')
  return React.createElement('input', {
    onChange: (e: any) => {
      console.log('📝 Input change (stub):', e.target.value)
      onChange?.(e)
    },
    className: "px-3 py-2 border rounded w-full",
    ...props
  })
}

export const BirhausCardStub = ({ children, ...props }: any) => {
  console.warn('🔧 BIRHAUS: Using BirhausCardStub - replace with full component in production')
  return React.createElement('div', {
    className: "p-4 border rounded bg-white",
    ...props
  }, children)
}

// Export all stubs for convenience
export const BirhausStubs = {
  // Providers
  ToastProvider: BirhausToastProviderStub,
  
  // Hooks
  useToast: useBirhausToastStub,
  useButton: useBirhausButtonStub,
  useDataTable: useBirhausDataTableStub,
  useSearch: useBirhausSearchStub,
  useForm: useBirhausFormStub,
  useAlert: useBirhausAlertStub,
  
  // Components
  Button: BirhausButtonStub,
  Input: BirhausInputStub,
  Card: BirhausCardStub,
  
  // Global utilities
  toast: toastStub
}

export default BirhausStubs